import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { EventInstance } from '@pacer/shared';
import * as api from '../api';
import { Icon, weatherIcon } from '../components/Icon';
import { useNow } from '../state/useNow';
import { queryKeys } from '../state/queries';
import { fmtDayLabel, fmtRange, useZone } from '../lib/dates';

/**
 * Idle mode. The wall stops being an app and becomes a picture frame that happens to know
 * what's next — big clock, the weather, the next few events, over a slow photo fade.
 *
 * Any touch dismisses it (handled by useIdle upstream), so there's no "exit" affordance to
 * clutter the frame.
 */
export function Screensaver({ intervalSeconds }: { intervalSeconds: number }) {
  const zone = useZone();
  const now = useNow(1000);
  const [index, setIndex] = useState(0);

  const { data: photos = [] } = useQuery({ queryKey: queryKeys.photos, queryFn: api.getPhotos });
  const { data: weather } = useQuery({ queryKey: queryKeys.weather, queryFn: api.getWeather });
  const { data: messages = [] } = useQuery({ queryKey: queryKeys.messages, queryFn: api.getMessages });
  const pinned = messages.filter((m) => m.pinned).slice(0, 3);

  const { data: countdowns = [] } = useQuery({ queryKey: queryKeys.countdowns, queryFn: api.getCountdowns });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  // Nearest few things to look forward to (countdowns + next birthdays), soonest first.
  const soon = useMemo(() => {
    const list: Array<{ title: string; days: number }> = [];
    const today = DateTime.now().setZone(zone).startOf('day');
    for (const c of countdowns) {
      const days = Math.round(DateTime.fromISO(c.targetDate, { zone }).startOf('day').diff(today, 'days').days);
      if (days >= 0) list.push({ title: c.title, days });
    }
    for (const m of members) {
      if (!m.birthday) continue;
      let next = DateTime.fromISO(m.birthday, { zone }).set({ year: today.year });
      if (next < today) next = next.plus({ years: 1 });
      list.push({ title: `${m.name}'s birthday`, days: Math.round(next.diff(today, 'days').days) });
    }
    return list.sort((a, b) => a.days - b.days).slice(0, 3);
  }, [countdowns, members, zone]);

  // Anchor BOTH ends to start-of-day. Deriving `to` from a live DateTime.now() would mint a
  // new query key every second (the clock re-renders each tick), leaving the events query
  // perpetually re-fetching and empty — so the agenda never appears.
  const today = DateTime.now().setZone(zone).startOf('day');
  const from = today.toUTC().toISO()!;
  const to = today.plus({ days: 3 }).toUTC().toISO()!;
  const { data: events = [] } = useQuery({
    queryKey: queryKeys.events(from, to, null),
    queryFn: () => api.getEvents(from, to, null),
  });

  const upcoming: EventInstance[] = useMemo(
    () => events.filter((e) => DateTime.fromISO(e.endTs) >= DateTime.now()).slice(0, 4),
    [events],
  );

  // Advance the slideshow. Re-keyed on photo count so deleting the last photo can't strand
  // the index past the end.
  useEffect(() => {
    if (photos.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % photos.length), intervalSeconds * 1000);
    return () => clearInterval(t);
  }, [photos.length, intervalSeconds]);

  useEffect(() => {
    if (index >= photos.length) setIndex(0);
  }, [photos.length, index]);

  const d = DateTime.fromJSDate(now).setZone(zone);

  return (
    <div className="fixed inset-0 z-60 overflow-hidden bg-black select-none">
      {/* All photos stay mounted and cross-fade on opacity — swapping one <img>'s src
          would flash the frame black while the next one decodes. */}
      {photos.map((p, i) => (
        <img
          key={p.id}
          src={p.url}
          alt=""
          className="absolute inset-0 size-full object-cover transition-opacity duration-[2000ms] ease-in-out"
          style={{ opacity: i === index ? 1 : 0 }}
          draggable={false}
        />
      ))}

      {/* Scrim: keeps white text legible over an unknown photo. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            photos.length > 0
              ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.15) 100%)'
              : 'radial-gradient(ellipse 80% 60% at 50% 40%, #14151c 0%, #000 100%)',
        }}
      />

      <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-14">
        <header className="flex items-start justify-between gap-6">
          <div>
            <div className="tabular font-mono text-[16vw] leading-[0.9] font-medium text-white md:text-[11vw]">
              {d.toFormat('h:mm')}
            </div>
            <p className="mt-3 font-display text-[5vw] text-white/70 md:text-[2.2vw]">
              {d.toFormat('cccc, LLLL d')}
            </p>
          </div>

          {weather && (
            <div className="flex shrink-0 items-center gap-3 text-white">
              <Icon name={weatherIcon(weather.now.code, weather.now.isDay)} size={44} strokeWidth={1.1} />
              <div className="text-right">
                <div className="tabular font-mono text-[5vw] leading-none md:text-[2.6vw]">
                  {weather.now.tempF}°
                </div>
                <div className="font-mono text-[12px] tracking-wide text-white/60 uppercase">
                  {weather.now.label}
                </div>
              </div>
            </div>
          )}
        </header>

        {/* Pinned notes float in the middle — "dinner's in the oven" is exactly the thing
            you want the wall to hold up while nobody's touching it. */}
        {pinned.length > 0 && (
          <div className="flex max-w-2xl flex-col gap-3">
            {pinned.map((m) => (
              <p key={m.id} className="font-display text-[4.5vw] leading-tight text-white md:text-[2vw]">
                {m.body}
              </p>
            ))}
          </div>
        )}

        {(upcoming.length > 0 || soon.length > 0) && (
          <footer className="flex flex-col gap-3">
            {soon.length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {soon.map((s) => (
                  <span key={s.title} className="font-mono text-[13px] text-white/60">
                    <span className="text-white/90">{s.title}</span> ·{' '}
                    {s.days === 0 ? 'today' : s.days === 1 ? 'tomorrow' : `${s.days} days`}
                  </span>
                ))}
              </div>
            )}
            {upcoming.length > 0 && (
              <>
                <span
                  className="font-mono text-[11px] text-white/50 uppercase"
                  style={{ letterSpacing: '0.22em' }}
                >
                  Coming up
                </span>
                {upcoming.map((ev) => (
                  <div key={ev.instanceId} className="flex items-baseline gap-4">
                    <span className="tabular w-40 shrink-0 font-mono text-[13px] text-white/60">
                      {fmtDayLabel(DateTime.fromISO(ev.startTs, { zone }), zone)} · {fmtRange(ev, zone)}
                    </span>
                    <span className="truncate font-display text-[3.4vw] text-white md:text-[1.6vw]">
                      {ev.title}
                    </span>
                  </div>
                ))}
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
